'use strict'

/* -----------------------------------------------------------------------------
 * dependencies
 * -------------------------------------------------------------------------- */

// 3rd party
import React from 'react'
import { useFormikContext } from 'formik'

// lib
import { accessibilityLabel } from '../../_support'
import { TextInput, TextInputProps } from '../inputs'
import {
  FieldContainer,
  FieldContainerProps,
  FieldErrorMessage,
  FieldErrorNotification,
  FieldIcon,
  FieldLabel,
  FieldPlaceholder,
  FieldPrefix,
  useField,
  UseFieldOptions
} from '../field'
import { fonts, colors } from '@theme'

type FontKeys = keyof typeof fonts

type ColorsKeys = keyof typeof colors

/* -----------------------------------------------------------------------------
 * TextField
 * -------------------------------------------------------------------------- */

export type TextFieldProps = FieldContainerProps &
  UseFieldOptions &
  TextInputProps & {
    label: string | null
    placeholder?: string
    showErrorMessage?: boolean
    submitOnEnter?: boolean
    fieldIcon?: React.ReactNode
    fieldPrefix?: React.ReactNode
    onIconPress?: () => void
    compact?: boolean
    // font from theme
    placeholderFont?: FontKeys | string
    labelFont?: FontKeys | string
    labelTextColor?: ColorsKeys | string
    labelBackgroundColor?: ColorsKeys | string
  }

const TextField = React.forwardRef(
  (
    {
      children,
      label,
      name,
      submitOnEnter,
      placeholder = '',
      showErrorMessage = true,
      fieldIcon,
      fieldPrefix,
      onIconPress,
      compact,
      placeholderFont,
      labelFont,
      labelTextColor,
      labelBackgroundColor,
      ...props
    }: TextFieldProps,
    ref: React.MutableRefObject<HTMLInputElement>
  ) => {
    const {
      fieldState,
      fieldError,
      fieldAutofilled,
      inputProps,
      containerProps
    } = useField({
      name,
      ...props
    })
    const { handleSubmit } = useFormikContext()
    const handleKeyPress = e => {
      if (submitOnEnter && e.key === 'Enter') {
        handleSubmit()
      }
    }

    const inputStyle = fieldPrefix
      ? [inputProps.style, { paddingLeft: 28 }]
      : inputProps.style

    return (
      <FieldContainer {...containerProps}>
        <TextInput
          ref={ref}
          onKeyPress={handleKeyPress}
          labeless={!label}
          compact={compact}
          {...accessibilityLabel(`${name} Input`)}
          {...inputProps}
          style={inputStyle}
        >
          {label && (
            <FieldLabel
              fieldState={fieldState}
              error={!!fieldError}
              font={labelFont as any}
              textColor={labelTextColor as any}
              backgroundColor={
                props.backgroundColor ||
                (labelBackgroundColor as any) ||
                'transparent'
              }
            >
              {label}
            </FieldLabel>
          )}
          {fieldPrefix && !fieldError && (
            <FieldPrefix compact={compact}>{fieldPrefix}</FieldPrefix>
          )}
          {fieldIcon && !fieldError && (
            <FieldIcon icon={fieldIcon} onPress={onIconPress} />
          )}
          {fieldError && <FieldErrorNotification />}
          {!fieldAutofilled &&
            fieldState === 'focused' &&
            inputProps.value === '' && (
              <FieldPlaceholder font={placeholderFont as any}>
                {placeholder}
              </FieldPlaceholder>
            )}
        </TextInput>
        {fieldError && showErrorMessage && (
          <FieldErrorMessage>{fieldError}</FieldErrorMessage>
        )}
      </FieldContainer>
    )
  }
)

export default TextField
